/**
 * Serverzustand des CRM.
 *
 * Die Kundenakte ist der Schluessel, an dem fast alles haengt: Ein neuer
 * Standort, ein geaenderter Kontakt, ein Vermerk -- alle veraendern sie.
 * Deshalb wird nach jedem Schreibvorgang der gesamte Zweig eines Kunden
 * ungueltig und nicht einzelne Teile davon. Eine Akte, die den soeben
 * angelegten Kontakt nicht zeigt, ist schlimmer als eine, die einen Moment
 * laenger laedt.
 */

import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { ApiError } from '../../lib/apiError';
import { ticketKeys } from '../tickets/queries';
import {
  anonymizeContact,
  checkContractExpiry,
  createActivity,
  createContact,
  createContract,
  createCustomer,
  createSite,
  deleteActivity,
  deleteContact,
  deleteContract,
  deleteCustomer,
  deleteSite,
  getCustomer,
  getCustomerFile,
  getCustomerTimeline,
  importContacts,
  importCustomers,
  listCustomers,
  previewContactImport,
  previewCustomerImport,
  searchContacts,
  suggestValues,
  updateContact,
  updateContract,
  updateCustomer,
  updateSite,
  type ActivityCreate,
  type ContactCreate,
  type ContactUpdate,
  type ContractCreate,
  type ContractUpdate,
  type CustomerCreate,
  type CustomerListQuery,
  type CustomerUpdate,
  type SiteCreate,
  type SiteUpdate,
  type SuggestionField,
} from './api';

export const crmKeys = {
  all: ['crm'] as const,
  customers: (query: CustomerListQuery) => ['crm', 'customers', query] as const,
  file: (customerId: string) => ['crm', 'file', customerId] as const,
  timeline: (customerId: string) => ['crm', 'timeline', customerId] as const,
};

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

export function useCustomerList(query: CustomerListQuery) {
  return useInfiniteQuery({
    queryKey: crmKeys.customers(query),
    queryFn: ({ pageParam }) => listCustomers(pageParam ? { ...query, cursor: pageParam } : query),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    retry: retryUnlessPermanent,
  });
}

/**
 * Die Nachschlagesuchen -- fuer Auswahlfelder mit Eingabe.
 *
 * **Gefragt wird erst, wenn jemand das Feld oeffnet** (``enabled``). Eine
 * Maske mit drei Auswahlfeldern loeste sonst beim Laden drei Suchen aus, von
 * denen meist keine gebraucht wird.
 *
 * Eigene Abfragen und nicht ``useCustomerList``: Die Liste blaettert und
 * haelt ihre Seiten; ein Auswahlfeld braucht die besten zwanzig zur aktuellen
 * Eingabe und behaelt beim Tippen die vorigen Treffer stehen, statt jedes Mal
 * kurz leer zu werden.
 */
export const LOOKUP_LIMIT = 20;

/**
 * Ein einzelner Kunde -- fuer die Anzeige einer Kennung als Name.
 *
 * Derselbe Schluessel wie in der Automatisierung (``ConditionValueText``):
 * Wer einen Kunden einmal nachgeschlagen hat, bekommt ihn dort und hier aus
 * demselben Zwischenspeicher.
 */
export function useCustomer(customerId: string) {
  return useQuery({
    queryKey: ['crm', 'customer', customerId],
    queryFn: () => getCustomer(customerId),
    enabled: customerId !== '',
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessPermanent,
  });
}

export function useCustomerLookup(
  term: string,
  status?: CustomerListQuery['status'],
  enabled = true,
) {
  return useQuery({
    queryKey: ['crm', 'lookup', 'customers', term, status ?? null],
    queryFn: () =>
      listCustomers({ q: term || undefined, status: status ?? undefined, limit: LOOKUP_LIMIT }),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: retryUnlessPermanent,
  });
}

export function useContactLookup(term: string, enabled = true) {
  return useQuery({
    queryKey: ['crm', 'lookup', 'contacts', term],
    queryFn: () => searchContacts(term),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: retryUnlessPermanent,
  });
}

/**
 * Vorschlaege fuer ein Freitextfeld aus dem Bestand -- Etappe 3 (F1-49).
 *
 * Eine Minute Ruhe: Branchen und Orte aendern sich nicht im Minutentakt, und
 * wer drei Kunden hintereinander anlegt, soll nicht dreimal fragen.
 */
export function useValueSuggestions(field: SuggestionField, term: string, enabled: boolean) {
  return useQuery({
    queryKey: ['crm', 'suggestions', field, term],
    queryFn: () => suggestValues(field, term),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: retryUnlessPermanent,
  });
}

export function useCustomerFile(customerId: string) {
  return useQuery({
    queryKey: crmKeys.file(customerId),
    queryFn: () => getCustomerFile(customerId),
    // Ohne Kennung wird nicht gefragt. Die Wissensdatenbank benutzt diesen
    // Aufruf auch ohne Kundenbezug -- eine Anfrage mit leerer Kennung waere
    // ein Fehlschlag, den niemand braucht.
    enabled: customerId !== '',
    retry: retryUnlessPermanent,
  });
}

export function useCustomerTimeline(customerId: string) {
  return useQuery({
    queryKey: crmKeys.timeline(customerId),
    queryFn: () => getCustomerTimeline(customerId),
    retry: retryUnlessPermanent,
  });
}

/**
 * Macht alles ungueltig, was an einem Kunden haengt.
 *
 * Die Akte fasst sechs Bestaende zusammen; welcher davon sich geaendert hat,
 * weiss der Aufrufer nicht immer -- ein Makro am Ticket beruehrt sie ebenso
 * wie ein neuer Vermerk.
 */
function useInvalidateCustomer() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: crmKeys.all });
  };
}

export function useCreateCustomer() {
  const invalidate = useInvalidateCustomer();
  return useMutation({
    mutationFn: (payload: CustomerCreate) => createCustomer(payload),
    onSuccess: invalidate,
  });
}

export function useUpdateCustomer() {
  const invalidate = useInvalidateCustomer();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, payload }: { customerId: string; payload: CustomerUpdate }) =>
      updateCustomer(customerId, payload),
    onSuccess: async () => {
      await invalidate();
      // Die VIP-Kennzeichnung steht an jedem Ticket dieses Kunden (F1-26).
      // Sie zu aendern, ohne die Ticketlisten nachzuziehen, hinterliesse
      // eine Hervorhebung, die erst beim naechsten Neuladen erscheint.
      await queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

export function useDeleteCustomer() {
  const invalidate = useInvalidateCustomer();
  return useMutation({
    mutationFn: (customerId: string) => deleteCustomer(customerId),
    onSuccess: invalidate,
  });
}

export function useCreateSite(customerId: string) {
  const invalidate = useInvalidateCustomer();
  return useMutation({
    mutationFn: (payload: SiteCreate) => createSite(customerId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateSite() {
  const invalidate = useInvalidateCustomer();
  return useMutation({
    mutationFn: ({ siteId, payload }: { siteId: string; payload: SiteUpdate }) =>
      updateSite(siteId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteSite() {
  const invalidate = useInvalidateCustomer();
  return useMutation({
    mutationFn: (siteId: string) => deleteSite(siteId),
    onSuccess: invalidate,
  });
}

export function useCreateContact(customerId: string) {
  const invalidate = useInvalidateCustomer();
  return useMutation({
    mutationFn: (payload: ContactCreate) => createContact(customerId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateContact() {
  const invalidate = useInvalidateCustomer();
  return useMutation({
    mutationFn: ({ contactId, payload }: { contactId: string; payload: ContactUpdate }) =>
      updateContact(contactId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteContact() {
  const invalidate = useInvalidateCustomer();
  return useMutation({
    mutationFn: (contactId: string) => deleteContact(contactId),
    onSuccess: invalidate,
  });
}

export function useAnonymizeContact() {
  const invalidate = useInvalidateCustomer();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (contactId: string) => anonymizeContact(contactId),
    onSuccess: async () => {
      await invalidate();
      // Der Melder steht am Ticket. Nach der Pseudonymisierung darf dort
      // nicht weiter der alte Name stehen.
      await queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

export function useCreateContract(customerId: string) {
  const invalidate = useInvalidateCustomer();
  return useMutation({
    mutationFn: (payload: ContractCreate) => createContract(customerId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateContract() {
  const invalidate = useInvalidateCustomer();
  return useMutation({
    mutationFn: ({ contractId, payload }: { contractId: string; payload: ContractUpdate }) =>
      updateContract(contractId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteContract() {
  const invalidate = useInvalidateCustomer();
  return useMutation({
    mutationFn: (contractId: string) => deleteContract(contractId),
    onSuccess: invalidate,
  });
}

export function useCheckContractExpiry() {
  const invalidate = useInvalidateCustomer();
  return useMutation({
    mutationFn: (leadDays: number) => checkContractExpiry(leadDays),
    onSuccess: invalidate,
  });
}

export function useCreateActivity(customerId: string) {
  const invalidate = useInvalidateCustomer();
  return useMutation({
    mutationFn: (payload: ActivityCreate) => createActivity(customerId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteActivity() {
  const invalidate = useInvalidateCustomer();
  return useMutation({
    mutationFn: (activityId: string) => deleteActivity(activityId),
    onSuccess: invalidate,
  });
}

// -- Import -------------------------------------------------------------------

export function usePreviewImport(kind: 'customers' | 'contacts') {
  return useMutation({
    mutationFn: (file: File) =>
      kind === 'customers' ? previewCustomerImport(file) : previewContactImport(file),
  });
}

export function useRunImport(kind: 'customers' | 'contacts') {
  const invalidate = useInvalidateCustomer();
  return useMutation({
    mutationFn: (file: File) =>
      kind === 'customers' ? importCustomers(file) : importContacts(file),
    onSuccess: invalidate,
  });
}
